/**
 * Formulario para crear/editar roles
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState, useEffect } from 'react';
import { usePermissionSelection } from '../../hooks';
import { Loading } from '../../components/UI/Loading';

const RoleForm = ({ role, mode = 'create', permissions = [], onSubmit, onCancel }) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    color: '#7B2D8E', // Color por defecto
    nivel_acceso: 2, // Nivel por defecto (Intermedio)
    is_active: true
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Hook para manejo de permisos
  const {
    selectedPermissions,
    togglePermission,
    selectAll,
    deselectAll,
    isPermissionSelected,
    setPermissions,
    clearSelection
  } = usePermissionSelection([], permissions);

  // Extraer acción del nombre del permiso (lo que está después del punto)
  const extractActionFromPermissionName = (permissionName) => {
    // Formato: "modulo.accion" -> extraer "accion"
    const parts = permissionName.split('.');
    if (parts.length >= 2) {
      return parts[1].toLowerCase();
    }
    return 'general';
  };

  // Obtener todas las acciones únicas de los permisos recibidos
  const getAllActions = () => {
    const actions = new Set();

    permissions.forEach(permission => {
      const action = extractActionFromPermissionName(permission.name);
      actions.add(action);
    });

    // Convertir a array y ordenar con acciones comunes primero
    const commonActions = ['view', 'create', 'update', 'delete'];
    const allActionsArray = Array.from(actions);

    const ordered = [];
    commonActions.forEach(action => {
      if (allActionsArray.includes(action)) {
        ordered.push(action);
      }
    });

    // Agregar el resto
    allActionsArray.forEach(action => {
      if (!commonActions.includes(action)) {
        ordered.push(action);
      }
    });

    return ordered;
  };

  // Normalizar nombre de módulo para que coincida con nuestro filtro
  const normalizeModuleName = (moduleName) => {
    const normalized = (moduleName || 'sistema').toLowerCase().trim();

    // Mapeo de variaciones de nombres de módulos del sistema RBAC
    const moduleMap = {
      'dashboard': 'dashboard',
      'clientes': 'clientes',
      'productos': 'productos',
      'ventas': 'ventas',
      'inventario': 'inventario',
      'proveedores': 'proveedores',
      'produccion': 'produccion',
      'vendedores': 'vendedores',
      'ordenes': 'ordenes',
      'reportes': 'reportes',
      'seguimiento': 'seguimiento',
      'admin': 'admin',
      // Alias alternativos
      'users': 'admin',
      'usuarios': 'admin',
      'clients': 'clientes',
      'cliente': 'clientes',
      'products': 'productos',
      'producto': 'productos',
      'sales': 'ventas',
      'venta': 'ventas',
      'suppliers': 'proveedores',
      'proveedor': 'proveedores',
      'production': 'produccion',
      'sellers': 'vendedores',
      'vendedor': 'vendedores',
      'orders': 'ordenes',
      'orden': 'ordenes',
      'reports': 'reportes',
      'reporte': 'reportes',
      'tracking': 'seguimiento',
      'administration': 'admin'
    };

    return moduleMap[normalized] || normalized;
  };

  // Agrupar permisos por módulo y acción
  const organizePermissionsByModule = () => {
    const modules = {};

    permissions.forEach(permission => {
      const originalModule = permission.module || 'sistema';
      const module = normalizeModuleName(originalModule);

      if (!modules[module]) {
        modules[module] = {
          name: module,
          displayName: getModuleDisplayName(module),
          permissions: {}
        };
      }

      // Extraer la acción del nombre del permiso (ej: "clientes.view" -> "view")
      const action = extractActionFromPermissionName(permission.name);

      // Agregar el permiso bajo su acción
      // Si ya existe un permiso para esta acción, mantener el primero
      if (!modules[module].permissions[action]) {
        modules[module].permissions[action] = permission;
      }
    });

    return modules;
  };

  // Orden de módulos del sistema RBAC
  const getOrderedModules = (modulesObj) => {
    const moduleOrder = [
      'dashboard',
      'clientes',
      'productos',
      'ventas',
      'inventario',
      'proveedores',
      'produccion',
      'vendedores',
      'ordenes',
      'reportes',
      'seguimiento',
      'admin'
    ];

    const ordered = [];
    const entries = Object.entries(modulesObj);

    // Agregar módulos en el orden especificado
    moduleOrder.forEach(moduleName => {
      const entry = entries.find(([key]) => key.toLowerCase() === moduleName);
      if (entry) {
        ordered.push(entry);
      }
    });

    // Agregar cualquier módulo adicional que no esté en el orden
    entries.forEach(entry => {
      const [key] = entry;
      if (!moduleOrder.includes(key.toLowerCase())) {
        ordered.push(entry);
      }
    });

    return ordered;
  };

  const allActions = getAllActions();
  const permissionsByModule = organizePermissionsByModule();
  const orderedModules = getOrderedModules(permissionsByModule);

  // Traducir nombres de acciones (acciones reales del sistema RBAC)
  const getActionDisplayName = (action) => {
    const translations = {
      'view': 'Ver',
      'create': 'Crear',
      'update': 'Editar',
      'delete': 'Eliminar',
      'export': 'Exportar',
      'notes': 'Notas',
      'emails': 'Emails',
      'formulas': 'Fórmulas',
      'invoices': 'Facturas',
      'movements': 'Movimientos',
      'quality': 'Calidad',
      'manage': 'Gestionar',
      'commissions': 'Comisiones',
      'approve': 'Aprobar',
      'financial': 'Financieros',
      'users': 'Usuarios',
      'roles': 'Roles',
      'permissions': 'Permisos',
      'settings': 'Configuración'
    };

    return translations[action] || action.charAt(0).toUpperCase() + action.slice(1);
  };

  // Debug: Ver estructura de permisos
  useEffect(() => {
    if (permissions.length > 0) {
      console.log('=== DEBUG PERMISOS ===');
      console.log('Total permisos recibidos:', permissions.length);

      // Ver todos los módulos originales únicos
      const uniqueModules = [...new Set(permissions.map(p => p.module))];
      console.log('Módulos ORIGINALES de la API:', uniqueModules);

      // Ver todos los grupos/acciones originales únicos
      const uniqueActions = [...new Set(permissions.map(p => p.group))];
      console.log('Acciones/Grupos ORIGINALES de la API:', uniqueActions);

      console.log('Primeros 5 permisos:', permissions.slice(0, 5));
      console.log('Acciones a mostrar (normalizadas):', allActions);
      console.log('Módulos después de normalizar:', Object.keys(permissionsByModule));
      console.log('Módulos filtrados (SOLO los 8):', orderedModules.map(([name]) => name));
      console.log('Total de módulos en tabla:', orderedModules.length);

      // Verificar cada módulo normalizado con detalle de sus permisos
      const moduleOrder = ['dashboard', 'clientes', 'productos', 'ventas', 'inventario', 'proveedores', 'produccion', 'vendedores', 'ordenes', 'reportes', 'seguimiento', 'admin'];
      console.log('Verificación de módulos después de normalizar:');
      moduleOrder.forEach(mod => {
        const found = Object.keys(permissionsByModule).find(key => key === mod);
        if (found) {
          const perms = permissionsByModule[found].permissions;
          const actions = Object.keys(perms);
          console.log(`  - ${mod}: ✓ encontrado con acciones: [${actions.join(', ')}]`);
        } else {
          console.log(`  - ${mod}: ✗ NO encontrado`);
        }
      });
    }
  }, [permissions]);

  // Inicializar formulario con datos del rol si es edición
  useEffect(() => {
    if (role && mode === 'edit') {
      setFormData({
        name: role.name || '',
        display_name: role.display_name || '',
        description: role.description || '',
        color: role.color || '#7B2D8E',
        is_active: role.is_active !== undefined ? role.is_active : true
      });

      // Establecer permisos seleccionados
      const rolePermissionIds = role.permissions ? role.permissions.map(p => p.id || p) : [];
      setPermissions(rolePermissionIds);
    } else {
      // Limpiar permisos en modo creación
      clearSelection();
    }
  }, [role, mode, setPermissions, clearSelection]);

  // Manejador de cambios en campos
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Generar nombre interno desde display_name
  const generateInternalName = (displayName) => {
    return displayName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9\s]/g, '') // Solo letras, números y espacios
      .replace(/\s+/g, '_') // Reemplazar espacios con guiones bajos
      .trim();
  };

  // Auto-generar nombre interno cuando cambia display_name
  const handleDisplayNameChange = (e) => {
    const displayName = e.target.value;
    setFormData(prev => ({
      ...prev,
      display_name: displayName,
      name: mode === 'create' ? generateInternalName(displayName) : prev.name
    }));

    if (errors.display_name) {
      setErrors(prev => ({
        ...prev,
        display_name: null
      }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    console.log('=== VALIDATING FORM ===');
    const newErrors = {};

    // Validaciones básicas
    if (!formData.name.trim()) {
      console.log('Validation error: name is empty');
      newErrors.name = 'El nombre es requerido';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      console.log('Validation error: name has invalid format');
      newErrors.name = 'El nombre solo puede contener letras minúsculas, números y guiones bajos';
    }

    if (!formData.display_name.trim()) {
      console.log('Validation error: display_name is empty');
      newErrors.display_name = 'El nombre para mostrar es requerido';
    }

    if (!formData.description.trim()) {
      console.log('Validation error: description is empty');
      newErrors.description = 'La descripción es requerida';
    }

    // VALIDACIÓN RELAJADA: Permitir crear roles sin permisos
    // El usuario puede agregar permisos después
    // if (selectedPermissions.length === 0) {
    //   console.log('Validation error: no permissions selected');
    //   newErrors.permissions = 'Debe seleccionar al menos un permiso';
    // }

    console.log('Validation errors found:', Object.keys(newErrors));
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== SUBMIT CLICKED ===');
    console.log('Form data:', formData);
    console.log('Selected permissions:', selectedPermissions);

    const isValid = validateForm();
    console.log('Form is valid:', isValid);
    console.log('Validation errors:', errors);

    if (!isValid) {
      console.log('Form validation failed, not submitting');
      return;
    }

    setSubmitting(true);
    console.log('Submitting to backend...');

    try {
      const roleData = {
        ...formData,
        permissions: selectedPermissions
      };

      console.log('Role data to send:', roleData);

      const result = await onSubmit(roleData);

      console.log('Backend response:', result);

      if (result && !result.success) {
        // Manejar errores de validación del servidor
        if (result.validationErrors) {
          setErrors(result.validationErrors);
        } else {
          setErrors({ general: result.message });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ general: 'Error procesando formulario' });
    } finally {
      setSubmitting(false);
    }
  };

  console.log('=== RoleForm Render ===');
  console.log('Permissions count:', permissions.length);
  console.log('Mode:', mode);
  console.log('Form data:', formData);

  // NO bloquear el renderizado si no hay permisos
  // Permitir crear roles sin permisos
  // if (permissions.length === 0) {
  //   console.log('No permissions loaded yet, showing loading...');
  //   return <Loading message="Cargando permisos..." />;
  // }

  console.log('Rendering form with', permissions.length, 'permissions');

  return (
    <div className="role-form">
      <form onSubmit={handleSubmit}>
        {/* Error general */}
        {errors.general && (
          <div className="alert alert-error mb-4">
            <i className="fas fa-exclamation-triangle"></i>
            {errors.general}
          </div>
        )}

        <div className="form-sections">
          {/* Información Básica */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-info-circle"></i>
              Información Básica
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="display_name" className="required">Nombre para Mostrar</label>
                <input
                  type="text"
                  id="display_name"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleDisplayNameChange}
                  className={`form-control ${errors.display_name ? 'is-invalid' : ''}`}
                  placeholder="Ej: Administrador, Vendedor, Supervisor"
                />
                {errors.display_name && (
                  <div className="invalid-feedback">{errors.display_name}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="name" className="required">Nombre Interno</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  placeholder="administrador, vendedor, supervisor"
                  readOnly={mode === 'edit'}
                />
                <small className="form-text text-muted">
                  {mode === 'edit' 
                    ? 'El nombre interno no se puede cambiar después de crear el rol'
                    : 'Se genera automáticamente. Solo letras minúsculas, números y guiones bajos'
                  }
                </small>
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description" className="required">Descripción</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                rows="3"
                placeholder="Describe las responsabilidades y funciones de este rol"
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>
          </div>

          {/* Estado del Rol */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-cogs"></i>
              Estado
            </h3>

            <div className="form-group">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label htmlFor="is_active" className="form-check-label">
                  Rol activo
                  <small className="help-text">Los roles inactivos no se pueden asignar a usuarios</small>
                </label>
              </div>
            </div>
          </div>

          {/* Permisos */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-shield-alt"></i>
              Permisos del Rol
              <div className="section-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success"
                  onClick={() => selectAll(permissions)}
                >
                  Seleccionar Todos
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  onClick={deselectAll}
                >
                  Deseleccionar Todos
                </button>
              </div>
            </h3>

            {errors.permissions && (
              <div className="alert alert-warning mb-3">
                <i className="fas fa-exclamation-triangle"></i>
                {errors.permissions}
              </div>
            )}

            <div className="permissions-container">
              <div className="permissions-summary">
                <span className="selected-count">
                  {selectedPermissions.length} de {permissions.length} permisos seleccionados
                </span>
              </div>

              {/* Tabla de permisos por módulo */}
              <div className="permissions-table-wrapper" style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <table className="permissions-table" style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '13px',
                        color: '#374151',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        width: '200px',
                        position: 'sticky',
                        left: 0,
                        background: '#f9fafb',
                        zIndex: 10
                      }}>Módulo</th>
                      {allActions.map(action => (
                        <th key={action} style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          minWidth: '100px'
                        }}>
                          {getActionDisplayName(action)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orderedModules.map(([moduleName, moduleData], index) => (
                      <tr key={moduleName} style={{
                        borderBottom: '1px solid #f3f4f6',
                        background: index % 2 === 0 ? 'white' : '#fafbfc'
                      }}>
                        <td style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          position: 'sticky',
                          left: 0,
                          background: index % 2 === 0 ? 'white' : '#fafbfc',
                          zIndex: 9
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className={`fas fa-${getModuleIcon(moduleName.toUpperCase())}`} style={{ color: '#7B2D8E' }}></i>
                            {moduleData.displayName}
                          </div>
                        </td>

                        {/* Generar celdas dinámicamente para cada acción */}
                        {allActions.map(action => (
                          <td key={action} style={{ padding: '14px 16px', textAlign: 'center' }}>
                            {moduleData.permissions[action] && (
                              <label className="permission-checkbox-simple">
                                <input
                                  type="checkbox"
                                  checked={isPermissionSelected(moduleData.permissions[action].id)}
                                  onChange={() => togglePermission(moduleData.permissions[action].id)}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                    accentColor: '#7B2D8E'
                                  }}
                                />
                              </label>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {mode === 'create' ? 'Creando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <i className={`fas ${mode === 'create' ? 'fa-plus' : 'fa-save'}`}></i>
                {mode === 'create' ? 'Crear Rol' : 'Guardar Cambios'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Función auxiliar para obtener nombre en español de módulos
const getModuleDisplayName = (module) => {
  const displayNames = {
    'dashboard': 'Dashboard',
    'clientes': 'Clientes',
    'productos': 'Productos',
    'ventas': 'Ventas',
    'inventario': 'Inventario',
    'proveedores': 'Proveedores',
    'produccion': 'Producción',
    'vendedores': 'Vendedores',
    'ordenes': 'Órdenes',
    'reportes': 'Reportes',
    'seguimiento': 'Seguimiento',
    'admin': 'Administración',
    // Módulos adicionales
    'usuarios': 'Usuarios',
    'roles': 'Roles',
    'permisos': 'Permisos',
    'compras': 'Compras',
    'preordenes': 'Pre-Órdenes',
    'cotizaciones': 'Cotizaciones',
    'facturacion': 'Facturación',
    'prospeccion': 'Prospección',
    'sistema': 'Sistema'
  };

  const normalized = module.toLowerCase().trim();
  return displayNames[normalized] || module.charAt(0).toUpperCase() + module.slice(1);
};

// Función auxiliar para obtener iconos de módulos
const getModuleIcon = (module) => {
  const icons = {
    'dashboard': 'tachometer-alt',
    'clientes': 'users',
    'productos': 'box',
    'ventas': 'shopping-cart',
    'inventario': 'warehouse',
    'proveedores': 'truck',
    'produccion': 'industry',
    'vendedores': 'user-tie',
    'ordenes': 'file-alt',
    'reportes': 'chart-bar',
    'seguimiento': 'search',
    'admin': 'cogs',
    // Módulos adicionales
    'usuarios': 'user',
    'roles': 'user-shield',
    'permisos': 'key',
    'compras': 'shopping-bag',
    'preordenes': 'clipboard-list',
    'cotizaciones': 'file-invoice',
    'facturacion': 'receipt',
    'prospeccion': 'search-plus',
    'sistema': 'cog'
  };

  const normalized = module.toLowerCase().trim();
  return icons[normalized] || 'folder';
};

export default RoleForm;
